import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtMoney, fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { Flag } from "@phosphor-icons/react";
import { toast } from "sonner";

const TABS = ["all", "draft", "sent", "overdue", "paid", "cancelled"];

export default function Invoices() {
  const [invs, setInvs] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [billingBusy, setBillingBusy] = useState(false);

  const load = () => api.get("/invoices").then((r) => setInvs(r.data)).catch(() => toast.error("Could not load invoices"));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);

  const toggleFlag = async (inv, e) => {
    e.preventDefault();
    await api.patch(`/invoices/${inv.id}`, { follow_up: !inv.follow_up });
    load();
  };

  const list = (invs || [])
    .filter((i) => tab === "all" || i.status === tab)
    .filter((i) => !q || [i.number, i.property_name, i.owner_name].join(" ").toLowerCase().includes(q.toLowerCase()));
  const outstanding = (invs || []).filter((i) => ["sent", "overdue"].includes(i.status)).reduce((s, i) => s + i.total, 0);

  return (
    <Layout title="Invoices">
      <div className="mb-4 rounded-md bg-primary p-5 text-primary-foreground" data-testid="ar-summary">
        <p className="label-caps !text-primary-foreground/70">Outstanding</p>
        <p className="font-heading text-3xl font-bold">{fmtMoney(outstanding)}</p>
      </div>
      <button data-testid="run-monthly-billing" disabled={billingBusy} onClick={async () => {
        setBillingBusy(true);
        try {
          const r = await api.post("/invoices/generate-monthly", {});
          toast.success(r.data.created ? `Created ${r.data.created} draft invoice${r.data.created > 1 ? "s" : ""} for ${r.data.month}` : `Nothing new to bill for ${r.data.month}`);
          load();
        } catch (e) { toast.error(fmtErr(e)); }
        setBillingBusy(false);
      }} className="mb-4 h-14 w-full rounded-md border border-primary font-bold text-primary transition-colors hover:bg-primary/5">
        {billingBusy ? "Running…" : "Run monthly billing (per-cut + seasonal installments)"}
      </button>
      <input data-testid="search-input" className="field-input mb-4 w-full" placeholder="Search invoice #, property, owner…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="mb-4 flex gap-2 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
            className={`h-12 shrink-0 rounded-md border px-4 text-sm font-bold capitalize ${tab === t ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{t}</button>
        ))}
      </div>
      <div className="space-y-2" data-testid="invoice-list">
        {!invs && <p className="text-muted-foreground">Loading…</p>}
        {invs && list.length === 0 && <p className="card-flat p-6 text-sm text-muted-foreground">No invoices here.</p>}
        {list.map((i) => (
          <Link key={i.id} to={`/invoices/${i.id}`} data-testid={`invoice-row-${i.id}`}>
            <div className={`card-flat flex items-center justify-between p-4 transition-colors hover:bg-muted/50 ${i.status === "overdue" ? "border-destructive" : ""}`}>
              <div>
                <p className="font-semibold">{i.number} · {i.property_name}</p>
                <p className="text-sm text-muted-foreground">{i.owner_name} {i.sent_at ? `· sent ${fmtDate(i.sent_at)}` : "· draft"}</p>
                {(i.last_reminded_at || i.last_resent_at) && ["sent", "overdue"].includes(i.status) && (
                  <p className="text-xs text-muted-foreground" data-testid={`reminder-info-${i.id}`}>
                    {i.last_reminded_at ? `Last reminded ${fmtDate(i.last_reminded_at)} · ${i.reminder_count || 1} sent${i.reminders_exhausted ? " · auto-reminders done — chase personally" : ""}` : ""}
                    {i.last_resent_at ? `${i.last_reminded_at ? " · " : ""}Re-sent ${fmtDate(i.last_resent_at)}${(i.resend_count || 0) > 1 ? ` ${i.resend_count}×` : ""}` : ""}
                  </p>
                )}
                {i.paid_at && <p className="text-xs text-primary font-semibold">Paid {fmtDate(i.paid_at)} via {i.payment_method}</p>}
                {i.status === "cancelled" && i.cancel_reason && <p className="text-xs font-semibold text-destructive" data-testid={`cancelled-reason-${i.id}`}>Cancelled: {i.cancel_reason}</p>}
              </div>
              <div className="flex items-center gap-3">
                {["sent", "overdue"].includes(i.status) && (
                  <button data-testid={`flag-${i.id}`} onClick={(e) => toggleFlag(i, e)}
                    title={i.follow_up ? (i.flag_source === "auto" ? "Auto-flagged — a payment reminder was emailed" : "Flagged by you") : "Flag for follow-up"}
                    className={i.follow_up ? "text-destructive" : "text-muted-foreground"}>
                    <Flag size={20} weight={i.follow_up ? "fill" : "regular"} />
                  </button>
                )}
                <div className="text-right">
                  <p className="font-bold">{fmtMoney(i.total)}</p>
                  <p className={`text-xs font-bold capitalize ${["overdue", "cancelled"].includes(i.status) ? "text-destructive" : i.status === "paid" ? "text-primary" : "text-muted-foreground"}`}>{i.status}</p>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Layout>
  );
}
